import { useEffect, useState } from 'react';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { roleAtLeast, type BoardSnapshot, type CardTemplate, type CardTemplateData } from '@kaba/shared';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { boardKey, queryClient } from '../../lib/board';
import { toast } from '../../lib/toast';
import { Button, cx, LABEL_BG, Sheet } from '../ui/ui';

const input = 'min-h-10 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 text-base sm:min-h-9 sm:text-sm';

/* ---------- Kartenvorlagen ---------- */

export function CardTemplatesSheet({ snapshot, open, onClose }: { snapshot: BoardSnapshot; open: boolean; onClose: () => void }) {
  const canEdit = roleAtLeast(snapshot.board.role, 'editor');
  const [editing, setEditing] = useState<CardTemplate | 'new' | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: boardKey(snapshot.board.id) });
  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setEditing(null);
          onClose();
        }
      }}
      title={editing ? (editing === 'new' ? 'Neue Kartenvorlage' : 'Kartenvorlage bearbeiten') : 'Kartenvorlagen'}
    >
      {editing ? (
        <CardTemplateEditor
          snapshot={snapshot}
          initial={editing === 'new' ? null : editing}
          onDone={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : (
        <div className="flex flex-col gap-3 p-4">
          <p className="text-sm text-[var(--ink-soft)]">
            Wiederkehrende Karten mit Checkliste, Labels und Frist in einem Schritt anlegen: „Karte hinzufügen“ → Vorlage. Jede Karte lässt sich auch
            über ihr Menü als Vorlage speichern.
          </p>
          {canEdit && (
            <Button variant="primary" className="self-start" onClick={() => setEditing('new')}>
              <Plus size={16} /> Neue Vorlage
            </Button>
          )}
          {!snapshot.cardTemplates.length && <p className="text-sm text-[var(--ink-soft)]">Noch keine Kartenvorlagen.</p>}
          <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
            {snapshot.cardTemplates.map((t) => (
              <li key={t.id} className="flex items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{t.name}</p>
                  <p className="truncate text-xs text-[var(--ink-soft)]">
                    {[
                      `„${t.data.title}“`,
                      t.data.checklist?.length ? `${t.data.checklist.length} Checklistenpunkte` : null,
                      t.data.dueInDays != null ? `Frist in ${t.data.dueInDays} Tagen` : null,
                      t.data.assignCreator ? 'mir zuweisen' : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                {canEdit && (
                  <>
                    <button aria-label={`„${t.name}“ bearbeiten`} className="inline-flex size-9 items-center justify-center rounded-md text-[var(--ink-soft)] hover:text-[var(--ink)]" onClick={() => setEditing(t)}>
                      <Pencil size={15} />
                    </button>
                    <button
                      aria-label={`„${t.name}“ löschen`}
                      className="inline-flex size-9 items-center justify-center rounded-md text-[var(--ink-soft)] hover:text-rose-600"
                      onClick={async () => {
                        if (!window.confirm(`Vorlage „${t.name}“ löschen?`)) return;
                        await api.del(`/api/card-templates/${t.id}`);
                        void refresh();
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Sheet>
  );
}

function CardTemplateEditor({ snapshot, initial, onDone }: { snapshot: BoardSnapshot; initial: CardTemplate | null; onDone: () => void }) {
  const d: CardTemplateData = initial?.data ?? { title: '' };
  const [name, setName] = useState(initial?.name ?? '');
  const [title, setTitle] = useState(d.title);
  const [checklist, setChecklist] = useState((d.checklist ?? []).join('\n'));
  const [labelIds, setLabelIds] = useState<string[]>(d.labelIds ?? []);
  const [due, setDue] = useState(d.dueInDays != null ? String(d.dueInDays) : '');
  const [priority, setPriority] = useState(d.priority ?? 0);
  const [assignCreator, setAssignCreator] = useState(!!d.assignCreator);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="flex flex-col gap-3 p-4"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        const data: CardTemplateData = {
          ...d,
          title: title.trim(),
          checklist: checklist.split('\n').map((x) => x.trim()).filter(Boolean),
          labelIds,
          dueInDays: due.trim() ? Math.max(0, Math.min(365, Math.floor(Number(due)))) : null,
          priority,
          assignCreator,
        };
        try {
          if (initial) await api.patch(`/api/card-templates/${initial.id}`, { name: name.trim(), data });
          else await api.post(`/api/boards/${snapshot.board.id}/card-templates`, { name: name.trim() || title.trim(), data });
          onDone();
        } catch (err) {
          toast.error((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <button type="button" onClick={onDone} className="inline-flex min-h-9 items-center gap-1 self-start text-sm text-[var(--ink-soft)] hover:text-[var(--ink)]">
        <ArrowLeft size={16} /> Zurück
      </button>
      <label className="flex flex-col gap-1.5 text-sm font-medium">
        Name der Vorlage
        <input className={input} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="z. B. Workshop" />
      </label>
      <label className="flex flex-col gap-1.5 text-sm font-medium">
        Kartentitel
        <input className={input} required value={title} maxLength={500} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="flex flex-col gap-1.5 text-sm font-medium">
        Checkliste (ein Punkt pro Zeile)
        <textarea className={cx(input, 'min-h-28 py-2')} value={checklist} onChange={(e) => setChecklist(e.target.value)} />
      </label>
      {snapshot.labels.length > 0 && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1 text-sm font-medium">Labels</legend>
          <div className="flex flex-wrap gap-1.5">
            {snapshot.labels.map((l) => {
              const on = labelIds.includes(l.id);
              return (
                <button
                  key={l.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setLabelIds((x) => (on ? x.filter((y) => y !== l.id) : [...x, l.id]))}
                  className={cx('inline-flex min-h-9 items-center gap-1.5 rounded-full border px-2.5 text-sm', on ? 'border-accent bg-accent-soft' : 'border-[var(--line)]')}
                >
                  <span className={cx('size-3 rounded-full', LABEL_BG[l.color])} aria-hidden />
                  {l.name || l.color}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm">
          Frist in
          <input type="number" min={0} max={365} className={cx(input, 'w-20')} value={due} onChange={(e) => setDue(e.target.value)} placeholder="–" />
          Tagen
        </label>
        <label className="flex items-center gap-2 text-sm">
          Priorität
          <select className={cx(input, 'w-auto')} value={priority} onChange={(e) => setPriority(Number(e.target.value))}>
            {['Keine', 'Niedrig', 'Mittel', 'Hoch', 'Dringend'].map((p, i) => (
              <option key={p} value={i}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="flex min-h-11 items-center gap-3 text-sm">
        <input type="checkbox" checked={assignCreator} onChange={(e) => setAssignCreator(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
        Wer die Karte anlegt, wird zuständig
      </label>
      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={busy || !title.trim()}>
          Speichern
        </Button>
      </div>
    </form>
  );
}

/* ---------- Board als Vorlage ---------- */

export function SaveBoardTemplateDialog({ snapshot, open, onClose }: { snapshot: BoardSnapshot; open: boolean; onClose: () => void }) {
  const { data: me } = useMe();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [includeCards, setIncludeCards] = useState(false);
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setName(snapshot.board.title);
      setDescription('');
      setIncludeCards(false);
      setShared(false);
    }
  }, [open, snapshot.board.title]);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Board als Vorlage speichern" side="center">
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await api.post(`/api/boards/${snapshot.board.id}/save-as-template`, { name: name.trim(), description: description.trim(), includeCards, shared });
            await queryClient.invalidateQueries({ queryKey: ['templates'] });
            toast.info(`Vorlage „${name.trim()}“ gespeichert – zu finden unter „Neues Board“.`);
            onClose();
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="text-sm text-[var(--ink-soft)]">
          Übernimmt Spalten (mit Regeln und WIP-Limits), Labels, eigene Felder, Kartenvorlagen und Automationen. Personen und Kommentare werden nicht
          übernommen.
        </p>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Name
          <input className={input} required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Beschreibung (optional)
          <input className={input} maxLength={300} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="flex min-h-11 items-center gap-3 text-sm">
          <input type="checkbox" checked={includeCards} onChange={(e) => setIncludeCards(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
          Offene Karten als Beispiel mitnehmen (Titel, Beschreibung, Checkliste, Labels)
        </label>
        {me?.isAdmin && (
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
            Für alle auf dieser Instanz anbieten
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
            Speichern
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
